/**
 * Credit-aware "view" of a Compare Sales response. This is the single
 * source of truth the Compare dialog reads when rendering visible
 * totals, delta chips, gutter match icons and the Payments & credits
 * breakdown.
 *
 * Why it exists: FPs emulate a Xero credit note by adding a negative
 * line item in Lightspeed. The backend detects this and stamps
 * ``is_credit_workaround`` on the matching items (see
 * ``backend/api/routes/inspector/exceptions/detail.py:284-289``) but
 * leaves the raw totals untouched - they're still used by the
 * exception taggers and the dialog-header mismatch chips.
 *
 * The Compare dialog wants a different read: drop the credit-workaround
 * line from the line-item table and surface it as a Credit entry in the
 * Payments & credits breakdown. ``totalPaid`` then includes the credit
 * amount; ``saleTotal`` reads as the gross sale before the credit was
 * applied. ``amountDue`` remains mathematically identical to the raw
 * version because both sides shifted by the same dollars - we're only
 * re-attributing the credit dollar from "negative line" to "credit
 * payment".
 *
 * The helpers in this file are pure (no React, no MUI) and are
 * unit-tested directly. Detection stays on the backend - this layer
 * consumes ``is_credit_workaround`` verbatim.
 */
import type {
  CompareSalesResponse,
  LineItemLS,
  LineItemXero,
} from '../../../../services/inspector/api'
import { xeroSettledTotal } from '../../../../utils/salesFormatters'
import {
  buildDisplayRows,
  DisplayRow,
  isSyntheticNoteRow,
} from '../../../../utils/saleDisplayRows'

const AMOUNT_TOLERANCE = 0.005

/**
 * A single row in the Payments & credits breakdown. Both LS payments
 * and Xero payments/credit-allocations collapse to this shape so the
 * renderer never has to branch on side-specific fields.
 */
export interface PaymentEntry {
  date: string | null
  amount: number
  label: string
  kind: 'payment' | 'credit'
  link?: string | null
}

export interface PaymentEntryPair {
  ls: PaymentEntry | null
  xero: PaymentEntry | null
  /** True iff both sides are present and amounts agree within
   *  ``AMOUNT_TOLERANCE``. Drives the centre gutter icon on the row. */
  matched: boolean
}

export type AmountDueState = 'paid_in_full' | 'outstanding' | 'overpaid'

export interface SaleCompareView {
  subtotalEx: number
  totalTax: number
  saleTotal: number
  paymentEntries: PaymentEntry[]
  entryCount: number
  totalPaid: number
  amountDue: number
  amountDueState: AmountDueState
}

const isCreditWorkaroundItem = (
  item: LineItemLS | LineItemXero | undefined,
): boolean => !!item?.is_credit_workaround

/**
 * Filter the buildDisplayRows() output down to the lines the
 * line-item table should render: drop synthetic notes (surfaced
 * separately above Totals) and drop credit-workaround pairs (surfaced
 * as Credit entries in Payments & credits). The backend mirrors
 * ``is_credit_workaround`` onto the paired Xero item when one exists
 * (detail.py:313-314), so checking either side is enough.
 */
export const deriveSaleLines = (data: CompareSalesResponse): DisplayRow[] =>
  buildDisplayRows(data).filter(
    r =>
      !isSyntheticNoteRow(r) &&
      !isCreditWorkaroundItem(r.lsItem) &&
      !isCreditWorkaroundItem(r.xeroItem),
  )

const buildLabel = (...parts: Array<string | null | undefined>): string =>
  parts.filter(Boolean).join(' ').trim()

const classifyAmountDue = (amountDue: number): AmountDueState => {
  if (amountDue < -AMOUNT_TOLERANCE) return 'overpaid'
  if (amountDue > AMOUNT_TOLERANCE) return 'outstanding'
  return 'paid_in_full'
}

// Credit notes are signed-negative documents, so the sale-centric mapping
// above (negative -> "Overpaid") reads wrong on the Xero credit-note side:
// an unsettled credit (negative amountDue) is credit still owed to the
// customer = "Outstanding", which is exactly how Xero's own credit-note view
// labels it. Invert the sign interpretation so it matches the source system
// instead of flipping to "Overpaid" once the line items are sign-aligned.
const classifyCreditNoteAmountDue = (amountDue: number): AmountDueState => {
  if (amountDue < -AMOUNT_TOLERANCE) return 'outstanding'
  if (amountDue > AMOUNT_TOLERANCE) return 'overpaid'
  return 'paid_in_full'
}

const isSyntheticOrCredit = (
  item: { is_synthetic_note?: boolean; is_credit_workaround?: boolean },
): boolean => !!item.is_synthetic_note || !!item.is_credit_workaround

const sumField = <T extends { is_synthetic_note?: boolean; is_credit_workaround?: boolean }>(
  items: T[],
  pick: (item: T) => number,
): number =>
  items.filter(i => !isSyntheticOrCredit(i)).reduce((s, i) => s + (pick(i) || 0), 0)

export const deriveLsSaleView = (data: CompareSalesResponse): SaleCompareView => {
  const { lightspeed } = data
  const subtotalEx = sumField(lightspeed.items, i => i.amount_ex_tax)
  const totalTax = sumField(lightspeed.items, i => i.tax)
  const saleTotal = sumField(lightspeed.items, i => i.amount_inc_tax)

  const paymentEntries: PaymentEntry[] = [
    ...(lightspeed.payments ?? []).map(
      (p): PaymentEntry => ({
        date: p.date ?? null,
        amount: p.amount,
        label: p.method || 'Payment',
        kind: 'payment',
      }),
    ),
    ...(lightspeed.credit_workaround_lines ?? []).map(
      (line): PaymentEntry => ({
        date: null,
        amount: Math.abs(line.amount),
        label: buildLabel('Credit', line.description),
        kind: 'credit',
      }),
    ),
  ]

  const totalPaid = paymentEntries.reduce((s, e) => s + e.amount, 0)
  const amountDue = saleTotal - totalPaid

  return {
    subtotalEx,
    totalTax,
    saleTotal,
    paymentEntries,
    entryCount: paymentEntries.length,
    totalPaid,
    amountDue,
    amountDueState: classifyAmountDue(amountDue),
  }
}

export const deriveXeroSaleView = (data: CompareSalesResponse): SaleCompareView => {
  const { xero } = data
  const subtotalEx = sumField(xero.items, i => i.amount_ex_tax)
  const totalTax = sumField(xero.items, i => i.tax)
  const saleTotal = sumField(xero.items, i => i.amount_inc_tax)

  const paymentEntries: PaymentEntry[] = [
    ...(xero.payments ?? []).map(
      (p): PaymentEntry => ({
        date: p.date ?? null,
        amount: p.amount,
        label: buildLabel(
          p.reference || 'Payment',
          p.account_code ? `(${p.account_code})` : null,
        ),
        kind: 'payment',
        link: p.link ?? null,
      }),
    ),
    ...(xero.credit_allocations ?? []).map(
      (a): PaymentEntry => ({
        date: a.date ?? null,
        amount: a.amount,
        label: buildLabel(
          'Credit',
          a.credit_note_number || a.credit_note_id?.slice(0, 8) || null,
        ),
        kind: 'credit',
        link: a.link ?? null,
      }),
    ),
  ]

  const totalPaid = paymentEntries.reduce((s, e) => s + e.amount, 0)
  const amountDue = saleTotal - totalPaid

  if (
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { DEV?: boolean } }).env?.DEV
  ) {
    const backendSettled =
      xero.settled_total ??
      xeroSettledTotal(xero.amount_paid, xero.amount_credited)
    if (Math.abs(totalPaid - backendSettled) > 0.01) {
      // Surface the divergence in dev so a missing payment / allocation
      // field gets caught quickly. Rendered total stays the entries sum.
      // eslint-disable-next-line no-console
      console.warn(
        '[saleCompareView] Xero entries totalPaid differs from settled_total',
        {
          totalPaid,
          backendSettled,
          xeroPayments: xero.payments?.length ?? 0,
          creditAllocations: xero.credit_allocations?.length ?? 0,
        },
      )
    }
  }

  return {
    subtotalEx,
    totalTax,
    saleTotal,
    paymentEntries,
    entryCount: paymentEntries.length,
    totalPaid,
    amountDue,
    // Credit-note side reads "Outstanding" (credit owed) for an unsettled
    // negative amountDue, matching Xero's own view - not the sale-centric
    // "Overpaid" the default classifier would return once the credit note
    // is sign-aligned.
    amountDueState:
      xero.kind === 'credit_note'
        ? classifyCreditNoteAmountDue(amountDue)
        : classifyAmountDue(amountDue),
  }
}

const dateDelta = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  if (!a || !b) return Number.POSITIVE_INFINITY
  const aMs = Date.parse(a)
  const bMs = Date.parse(b)
  if (Number.isNaN(aMs) || Number.isNaN(bMs)) return Number.POSITIVE_INFINITY
  return Math.abs(aMs - bMs)
}

/**
 * Visual-alignment pairing: walk LS entries, claim the closest unconsumed
 * Xero entry whose amount agrees within ``AMOUNT_TOLERANCE``; tie-break
 * on smallest absolute date delta. Anything left over on either side
 * becomes a one-sided pair so the renderer can mark the orphan.
 *
 * Pairing is rendering-only - it does not alter ``is_credit_workaround``
 * flags or any backend-derived semantic. Exported for unit testing
 * because pairing is the section most likely to grow edge cases.
 */
export const pairPaymentEntries = (
  lsEntries: PaymentEntry[],
  xeroEntries: PaymentEntry[],
): PaymentEntryPair[] => {
  const xeroTaken = new Array<boolean>(xeroEntries.length).fill(false)
  const pairs: PaymentEntryPair[] = []

  for (const ls of lsEntries) {
    let bestIdx = -1
    let bestDateDelta = Number.POSITIVE_INFINITY
    for (let i = 0; i < xeroEntries.length; i += 1) {
      if (xeroTaken[i]) continue
      const xero = xeroEntries[i]
      if (Math.abs(ls.amount - xero.amount) > AMOUNT_TOLERANCE) continue
      const d = dateDelta(ls.date, xero.date)
      if (d < bestDateDelta) {
        bestDateDelta = d
        bestIdx = i
      }
    }
    if (bestIdx >= 0) {
      xeroTaken[bestIdx] = true
      pairs.push({ ls, xero: xeroEntries[bestIdx], matched: true })
    } else {
      pairs.push({ ls, xero: null, matched: false })
    }
  }
  for (let i = 0; i < xeroEntries.length; i += 1) {
    if (!xeroTaken[i]) {
      pairs.push({ ls: null, xero: xeroEntries[i], matched: false })
    }
  }
  return pairs
}

/**
 * Match flag for one totals-block row. Centralised so the hook +
 * the totals block both apply the same tolerance.
 */
export const valuesMatch = (a: number, b: number): boolean =>
  Math.abs(a - b) < AMOUNT_TOLERANCE
